import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

export interface TourStep {
  id: string;
  title: string;
  description: string;
  targetSelector?: string; // CSS selector to highlight or point to
  route?: string; // Auto-navigate to this route if needed
  section?: string; // Optional query param ?section=
  actionHint?: string; // E.g. "Try pressing Cmd+K" or "Click on a tab group"
  badge?: string; // E.g. "Step 1 of 8"
  category: 'overview' | 'tabs' | 'ai' | 'apps' | 'shortcuts' | 'settings';
}

export const TOUR_STEPS: TourStep[] = [
  {
    id: 'welcome',
    title: 'Welcome to Khiimori',
    description:
      'Khiimori is your intelligent workspace that turns browser tabs into dynamic applications and structured knowledge. Let’s take an interactive walkthrough of how it works in real-time.',
    targetSelector: '[data-tour="sidebar-brand"]',
    route: '/home',
    badge: 'Introduction',
    category: 'overview',
    actionHint: 'Click Next to explore your intelligent workspace.',
  },
  {
    id: 'home-overview',
    title: 'Workspace Pulse & Stats',
    description:
      'The Home Hub summarizes your active tabs, sessions, focus cycles, and AI recommendations. Instant quick action banners let you jump straight into common workflows.',
    targetSelector: '[data-tour="home-quick-actions"]',
    route: '/home',
    badge: '1 · Home Hub',
    category: 'overview',
    actionHint: 'View real-time workspace activity and smart action cards.',
  },
  {
    id: 'tab-monitor',
    title: 'Tabs Hub & Entropy Management',
    description:
      'The Tabs Hub monitors your open browser tabs, clusters related research sessions, analyzes tab entropy, and cleans up tab clutter with automatic AI grouping.',
    targetSelector: '[data-tour="nav-tabs"]',
    route: '/tabs',
    badge: '2 · Tabs & Workflows',
    category: 'tabs',
    actionHint: 'See live open tabs, cluster suggestions, and tab memories.',
  },
  {
    id: 'ai-hub',
    title: 'AI Hub & Multi-Agent Capabilities',
    description:
      'Chat directly with your open tab context, trigger proactive workspace insights, and run compound agentic workflows across all your active research.',
    targetSelector: '[data-tour="nav-ai"]',
    route: '/ai',
    badge: '3 · AI Intelligence',
    category: 'ai',
    actionHint: 'Ask questions about any open tab or run multi-agent automations.',
  },
  {
    id: 'apps-hub',
    title: 'Apps Hub & Dynamic App Generator',
    description:
      'Transform raw tabs into interactive React apps, side-by-side comparison tables, interactive timelines, and synthesized research papers with one click.',
    targetSelector: '[data-tour="nav-apps"]',
    route: '/apps',
    badge: '4 · App Generation',
    category: 'apps',
    actionHint: 'Generate full interactive tools directly from tab content.',
  },
  {
    id: 'command-palette',
    title: 'Global Command Palette (Cmd + K)',
    description:
      'Your universal launcher. Hit Cmd+K (or Ctrl+K) anytime to search hubs, execute agent skills, jump to deep pages, or switch UI modes in milliseconds.',
    targetSelector: '[data-tour="nav-shortcuts"]',
    badge: '5 · Keyboard Power',
    category: 'shortcuts',
    actionHint: 'Press Cmd+K or Ctrl+K anytime from anywhere in Khiimori.',
  },
  {
    id: 'ai-assistant-toggle',
    title: 'Ambient AI Assistant (Ctrl + J)',
    description:
      'A persistent companion button is always available at the bottom corner for immediate conversational assistance and workspace suggestions without losing your place.',
    targetSelector: '[data-tour="ai-assistant-btn"]',
    badge: '6 · Quick Assistant',
    category: 'ai',
    actionHint: 'Click the glowing bulb or press Ctrl+J to open the AI assistant drawer.',
  },
  {
    id: 'settings-and-skills',
    title: 'Skill Library & Agent Orchestration',
    description:
      'Customize themes, switch between Explicit/Ambient/Invisible UI modes, manage API keys, and orchestrate dozens of autonomous background skills in Settings.',
    targetSelector: '[data-tour="nav-settings"]',
    route: '/settings',
    badge: '7 · Settings & Skills',
    category: 'settings',
    actionHint: 'Access the Skill Library, API providers, and session security.',
  },
];

interface GuideTourContextType {
  isTourActive: boolean;
  currentStepIndex: number;
  currentStep: TourStep;
  totalSteps: number;
  startTour: (startIndex?: number) => void;
  stopTour: () => void;
  nextStep: () => void;
  prevStep: () => void;
  goToStep: (index: number) => void;
  hasSeenTour: boolean;
  markTourCompleted: () => void;
}

const GuideTourContext = createContext<GuideTourContextType | null>(null);

const STORAGE_TOUR_SEEN = 'khiimori.tourSeen';

export function GuideTourProvider({ children }: { children: React.ReactNode }) {
  const [isTourActive, setIsTourActive] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [hasSeenTour, setHasSeenTour] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_TOUR_SEEN) === '1';
    } catch {
      return false;
    }
  });

  const navigate = useNavigate();
  const location = useLocation();

  const totalSteps = TOUR_STEPS.length;
  const currentStep = TOUR_STEPS[currentStepIndex] || TOUR_STEPS[0];

  const navigateToStepRoute = useCallback(
    (step: TourStep) => {
      if (step.route && location.pathname !== step.route) {
        const target = step.section ? `${step.route}?section=${step.section}` : step.route;
        navigate(target);
      }
    },
    [location.pathname, navigate]
  );

  const startTour = useCallback(
    (startIndex = 0) => {
      const idx = Math.max(0, Math.min(startIndex, totalSteps - 1));
      setCurrentStepIndex(idx);
      setIsTourActive(true);
      navigateToStepRoute(TOUR_STEPS[idx]);
    },
    [totalSteps, navigateToStepRoute]
  );

  const markTourCompleted = useCallback(() => {
    try {
      localStorage.setItem(STORAGE_TOUR_SEEN, '1');
      setHasSeenTour(true);
    } catch {
      // ignore
    }
  }, []);

  const stopTour = useCallback(() => {
    setIsTourActive(false);
    markTourCompleted();
  }, [markTourCompleted]);

  const nextStep = useCallback(() => {
    if (currentStepIndex < totalSteps - 1) {
      const nextIdx = currentStepIndex + 1;
      setCurrentStepIndex(nextIdx);
      navigateToStepRoute(TOUR_STEPS[nextIdx]);
    } else {
      stopTour();
    }
  }, [currentStepIndex, totalSteps, navigateToStepRoute, stopTour]);

  const prevStep = useCallback(() => {
    if (currentStepIndex > 0) {
      const prevIdx = currentStepIndex - 1;
      setCurrentStepIndex(prevIdx);
      navigateToStepRoute(TOUR_STEPS[prevIdx]);
    }
  }, [currentStepIndex, navigateToStepRoute]);

  const goToStep = useCallback(
    (index: number) => {
      if (index >= 0 && index < totalSteps) {
        setCurrentStepIndex(index);
        navigateToStepRoute(TOUR_STEPS[index]);
      }
    },
    [totalSteps, navigateToStepRoute]
  );

  // Keyboard navigation for guide tour
  useEffect(() => {
    if (!isTourActive) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        stopTour();
      } else if (e.key === 'ArrowRight') {
        nextStep();
      } else if (e.key === 'ArrowLeft') {
        prevStep();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isTourActive, nextStep, prevStep, stopTour]);

  return (
    <GuideTourContext.Provider
      value={{
        isTourActive,
        currentStepIndex,
        currentStep,
        totalSteps,
        startTour,
        stopTour,
        nextStep,
        prevStep,
        goToStep,
        hasSeenTour,
        markTourCompleted,
      }}
    >
      {children}
    </GuideTourContext.Provider>
  );
}

export function useGuideTour() {
  const context = useContext(GuideTourContext);
  if (!context) {
    throw new Error('useGuideTour must be used within a GuideTourProvider');
  }
  return context;
}
